/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { restartableTask } from 'ember-concurrency';
import CustomerModel from 'cio/models/customer';
import ApiErrorsService from 'cio/services/api-errors';
import ApiService from 'cio/services/api';
import AuthService from 'cio/services/auth';
import { IdentifierKey } from 'cio/utils/identifiers';
import { CUSTOMER_TIME_ZONE } from 'cio/utils/time-zones';
import { FrequencyOption } from 'cio/utils/campaigns/date-triggered';
import { formatWithTimezone, DEFAULT_TIMESTAMP_FORMAT } from 'cio/utils/format-with-timezone';
import moment from 'moment-timezone';

type MatchTime = {
  attributeTimestamp: number;
  futureTimestamp: number;
  nextTimestamp: number;
};

interface CampaignsTriggerSetupDatePreviewArgs {
  attribute: string;
  attributeDisplayValue: string;
  fallbackZone: string;
  frequency: FrequencyOption;
  people?: CustomerModel[];
  leadDays: number;
  startTime: string;
  zone: string;
  isNestedAttribute: boolean;
}

export default class CampaignsTriggerSetupDatePreview extends Component<CampaignsTriggerSetupDatePreviewArgs> {
  @service api!: ApiService;
  @service auth!: AuthService;
  @service apiErrors!: ApiErrorsService;

  @tracked matchTime: MatchTime | null = null;
  @tracked matchTimeError = '';
  @tracked selectedPerson?: CustomerModel;

  get attributeDateWithTime() {
    return formatWithTimezone(this.matchTime?.attributeTimestamp, this.timezone);
  }

  get entryDateWithTime() {
    return formatWithTimezone(this.matchTime?.nextTimestamp, this.timezone);
  }

  get reentryDateWithTime() {
    return formatWithTimezone(this.matchTime?.futureTimestamp, this.timezone);
  }

  get timezone() {
    let { fallbackZone, zone } = this.args;
    if (zone === CUSTOMER_TIME_ZONE.value) {
      return (this.selectedPerson?.attrs.timezone as string) ?? fallbackZone;
    }

    return zone;
  }

  get entryDateWithTimeValid(): boolean {
    if (this.entryDateWithTime == null) {
      return false;
    }

    return moment(this.entryDateWithTime, DEFAULT_TIMESTAMP_FORMAT).isValid();
  }

  get reentryText(): string {
    let { frequency } = this.args;

    if (this.matchTime?.futureTimestamp == null || frequency === FrequencyOption.once) {
      return '';
    }

    let timestamp = moment.unix(this.matchTime.futureTimestamp);
    let timeframe = '';
    let date = '';
    if (frequency === FrequencyOption.monthly) {
      timeframe = 'month';
      date = timestamp.format('Do');
    } else if (frequency === FrequencyOption.yearly) {
      timeframe = 'year';
      date = timestamp.format('MMM Do');
    }
    if (timeframe !== '' && date !== '') {
      return `and every following ${timeframe} on the ${date} if the person is not in the campaign's workflow. If they are, that ${timeframe} will be skipped, and they will re-enter the following ${timeframe}`;
    }
    return '';
  }

  @action
  onPersonChange(person: CustomerModel) {
    this.selectedPerson = person;
    this.fetchPersonMatchTimeTask.perform(person);
  }

  @action
  onUpdate() {
    if (this.selectedPerson != null) {
      this.fetchPersonMatchTimeTask.perform(this.selectedPerson);
      return;
    }

    let person = this.args.people?.[0];
    if (person != null && this.selectedPerson == null) {
      this.selectedPerson = person;
      this.fetchPersonMatchTimeTask.perform(person);
    }
  }

  fetchPersonMatchTimeTask = restartableTask(async (person: CustomerModel) => {
    let isCustomerTimezone = CUSTOMER_TIME_ZONE.value === this.args.zone;
    let zone = isCustomerTimezone ? (person.attrs.timezone as string) ?? this.args.fallbackZone : this.args.zone;

    try {
      let response = await this.api.post('profile/profile_matchtime', {
        data: {
          customer_cio_id: person.identifiers[IdentifierKey.CioId],
          date_triggered_attribute: this.args.isNestedAttribute ? this.args.attributeDisplayValue : this.args.attribute,
          date_triggered_frequency: this.args.frequency,
          date_triggered_lead_days: this.args.leadDays,
          date_triggered_start_time: this.args.startTime,
          date_triggered_customer_tz: isCustomerTimezone,
          date_triggered_zone: zone,
        },
        ignoreErrors: true,
        includeStatus: true,
      });

      this.matchTimeError = '';
      this.matchTime = {
        attributeTimestamp: Number(response.attribute_timestamp),
        futureTimestamp: Number(response.future_timestamp),
        nextTimestamp: Number(response.next_timestamp),
      };
    } catch (e) {
      if (e.status !== '422' && e.source == null) {
        this.apiErrors.report(e);
      }

      this.matchTimeError = e.errors[0].detail;
      this.matchTime = null;
    }
  });
}
